import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import {
  AppBar,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import * as S from './styled.js';
import hiCellLogo from 'assets/hiCellLogo.png';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Creators as StoreCategoryCreators } from 'store/categories/categories-duck.js';
import { Creators as StoreSettingsCreators } from 'store/settings/settings-duck.js';
import PropTypes from 'prop-types';
import Footer from './footer.jsx';
import { debounce } from 'lodash';
import Cookies from 'js-cookie';

const StoreTemplate = ({ menuCategories, categoriesFetchMenuCategories, settingsFetchAsync }) => {
  const [open, setOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  const debouncedSearch = useCallback(
    debounce((value) => {
      const searchParams = new URLSearchParams(location.search);

      // Atualiza o searchTerm sem perder os outros parâmetros
      if (value) {
        searchParams.set('searchTerm', value);
      } else {
        searchParams.delete('searchTerm');
      }

      navigate(`/products?${searchParams.toString()}`);
    }, 500),
    [navigate, location.search],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value.trim());
  };

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev?.filter((item) => item !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    if (!menuCategories.length > 0) {
      categoriesFetchMenuCategories();
    }
    if (
      !Cookies.get('whatsapp_link') &&
      !Cookies.get('whatsapp_message') &&
      !Cookies.get('whatsapp_product_message')
    ) {
      settingsFetchAsync();
    }
  }, [categoriesFetchMenuCategories, settingsFetchAsync]);

  const handleNavigate = (params) => {
    navigate(`/products?${params}`);
    setOpen(false);
  };

  return (
    <>
      <AppBar position="fixed">
        <S.NavBarContainer>
          <Link to="/">
            <S.Logo src={hiCellLogo} alt="Logo" />
          </Link>
          <S.MenuButton onClick={toggleDrawer(true)}>
            <MenuIcon />
          </S.MenuButton>

          <Drawer open={open} onClose={toggleDrawer(false)}>
            <Box sx={S.drawerStyled}>
              <List>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigate('/trade-form');
                      setOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      <SmartphoneIcon sx={{ color: '#92b21f' }} />
                    </ListItemIcon>
                    <ListItemText>Faça uma simulação de troca</ListItemText>
                  </ListItemButton>
                </ListItem>
                {menuCategories?.map((item) => (
                  <Box key={item.id}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleNavigate(`categoryId=${item.id}`)}>
                        <ListItemIcon>
                          <SmartphoneIcon sx={{ color: '#92b21f' }} />
                        </ListItemIcon>
                        <ListItemText primary={item.name} />
                        {item.subCategories?.length > 0 && (
                          <IconButton
                            sx={{ color: '#92b21f' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(item.id);
                            }}
                          >
                            {expandedIds.includes(item.id) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={expandedIds.includes(item.id)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 4 }}>
                        {item.subCategories?.map((sub) => (
                          <ListItem key={sub.id} disablePadding>
                            <ListItemButton
                              onClick={() =>
                                handleNavigate(`categoryId=${item.id}&subCategoryId=${sub.id}`)
                              }
                            >
                              <ListItemText primary={sub.name} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                ))}
              </List>
            </Box>
          </Drawer>

          <S.NavItem href={`/trade-form`}>Faça uma simulação de troca</S.NavItem>
          {menuCategories?.map((item) => (
            <S.NavItemWithSub key={item.id}>
              <S.NavItem href={`/products?categoryId=${item.id}`}>{item.name}</S.NavItem>

              {item.subCategories?.length > 0 && (
                <S.SubMenu>
                  {item.subCategories.map((sub) => (
                    <S.SubMenuItem
                      key={sub.id}
                      href={`/products?categoryId=${item.id}&subCategoryId=${sub.id}`}
                    >
                      {sub.name}
                    </S.SubMenuItem>
                  ))}
                </S.SubMenu>
              )}
            </S.NavItemWithSub>
          ))}

          <S.SearchInput
            placeholder="Pesquisar..."
            value={searchValue}
            onChange={handleSearchChange}
          />
        </S.NavBarContainer>
      </AppBar>

      <Container style={S.containerStyled}>
        <Outlet />
      </Container>

      <Footer menuCategories={menuCategories} />
    </>
  );
};

const mapStateToProps = (state) => ({
  menuCategories: state.Category.menuCategories,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...StoreCategoryCreators, ...StoreSettingsCreators }, dispatch);

StoreTemplate.propTypes = {
  menuCategories: PropTypes.array.isRequired,
  categoriesFetchMenuCategories: PropTypes.func.isRequired,
  settingsFetchAsync: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(StoreTemplate);
